import { useState, useEffect } from 'react';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '../../../Components/UI/dialog';
import { Button } from '../../../Components/UI/button';
import { Input } from '../../../Components/UI/input';
import { Label } from '../../../Components/UI/label';
import { X, Plus, Trash2, Check, Pencil } from 'lucide-react';
import { toast } from 'sonner';
import { AlertBox } from '../../../Components/Common/AlertBox';
import apiService from '../../../Services/APIService';
import { useSelector } from 'react-redux';

const threadTypNm = {
  1: 'Inquiry',
  2: 'Order',
  3: 'Support Ticket'
};

// API function
export const updateThreadFieldAPI = async (changeType, key, value, values = null) => {
  try {
    const configData = {
      changeType: "THRTYP",
      key: key,
      value: value,
      values: values || ""
    };
    const result = await apiService.sendChange(3, "set", configData);
    return result;
  } catch (error) {
    console.error(`Failed to update thread field:`, error);
    throw error;
  }
};

const ThrFieldConfig = ({ 
  open, 
  onClose, 
  threadType, 
  existingFields = [] // Array of {FID, F} - this is now optional
}) => {
  const [fields, setFields] = useState([]);
  const [showAddField, setShowAddField] = useState(false);
  const [fieldName, setFieldName] = useState('');
  const [editingField, setEditingField] = useState(null);
  const [deleteAlertOpen, setDeleteAlertOpen] = useState(false);
  const [fieldToDelete, setFieldToDelete] = useState(null);
  const [loading, setLoading] = useState(false);

  const maxFields = 5;
  const threadTypeName = threadTypNm[threadType] || 'Unknown';

  const threadTypeData = useSelector(state => state.masterData.threadTypes);

  useEffect(() => {
    if (open) {
      try {
        // Find the thread type matching the current threadType from Redux store
        const currentThreadType = threadTypeData.find(tt => tt.TID === threadType);
        if (currentThreadType && currentThreadType.Fields) {
          // Set fields from the Redux store thread type data  
          const fieldsData = typeof currentThreadType.Fields === "string" ? JSON.parse(currentThreadType.Fields) : currentThreadType.Fields;

          setFields(fieldsData);
        } else {
          // If no fields are found for this thread type, fallback to existingFields or empty array
          setFields(existingFields || []);
        }
      } catch (error) {
        console.error('Error loading thread fields:', error);
        // Fallback to existingFields prop or empty array
        setFields(existingFields || []);
      }
    }
  }, [open, threadType, existingFields, threadTypeData]);

  const handleAddField = async () => {
    if (!fieldName.trim()) {
      toast.error('Please enter a field name.');
      return;
    }

    // Check for duplicate field names
    if (fields.some(f => f.F.toLowerCase() === fieldName.trim().toLowerCase())) {
      toast.error('A field with this name already exists.');
      return;
    }

    if (fields.length >= maxFields) {
      toast.error(`Maximum ${maxFields} fields allowed.`);
      return;
    }

    try {
      setLoading(true);
      
      // Generate new FID (max FID + 1)
      const newFID = fields.length > 0 ? Math.max(...fields.map(f => f.FID)) + 1 : 1;
      
      const newField = {
        FID: newFID,
        F: fieldName.trim()
      };

      // Call API to add field
      await updateThreadFieldAPI(
        "THRTYP",
        "ADDFIELD",
        "",
        JSON.stringify({ TID: threadType, FID: '', F: fieldName.trim() })
      );

      setFields([...fields, newField]);
      toast.success('Field added.');
      setFieldName('');
      setShowAddField(false);
    } catch (error) {
      toast.error(error.message || 'Failed to add field.');
    } finally {
      setLoading(false);
    }
  };

  const handleUpdateField = async () => {
    if (!fieldName.trim()) {
      toast.error('Please enter a field name.');
      return;
    }

    // Check for duplicate field names (excluding current field)
    if (fields.some(f => f.FID !== editingField.FID && f.F.toLowerCase() === fieldName.trim().toLowerCase())) {
      toast.error('A field with this name already exists.');
      return;
    }

    try {
      setLoading(true);

      // Call API to update field
      await updateThreadFieldAPI(
        "THRTYP",
        "UPDFIELD",
        "",
        JSON.stringify({ TID: threadType, FID: editingField.FID, F: fieldName.trim() })
      );

      const updatedFields = fields.map(f => 
        f.FID === editingField.FID ? { ...f, F: fieldName.trim() } : f
      );
      setFields(updatedFields);
      toast.success('Field updated.');
      setFieldName('');
      setEditingField(null);
      setShowAddField(false);
    } catch (error) {
      toast.error(error.message || 'Failed to update field.');
    } finally {
      setLoading(false);
    }
  };

  const handleDeleteField = async () => {
    if (!fieldToDelete) return;

    try {
      setLoading(true);

      // Call API to delete field
      await updateThreadFieldAPI(
        "THRTYP",
        "DLTFIELD",
        "",
        JSON.stringify({ TID: threadType, FID: fieldToDelete.FID, F: '' })
      );

      setFields(fields.filter(f => f.FID !== fieldToDelete.FID));
      toast.success('Field deleted.');
      setDeleteAlertOpen(false);
      setFieldToDelete(null);
    } catch (error) {
      toast.error(error.message || 'Failed to delete field.');
    } finally {
      setLoading(false);
    }
  };

  const handleClose = () => {
    setFieldName('');
    setShowAddField(false);
    setEditingField(null);
    setFields([]);
    onClose();
  };

  const handleCloseAddForm = () => {
    setFieldName('');
    setShowAddField(false);
    setEditingField(null);
  };

  const renderFieldList = () => (
    <div className="space-y-4">
      <p className="text-xs text-gray-600">
        <span className="font-medium">Note:</span> These fields will be visible to all visitors for this {threadTypeName.toLowerCase()} type.
      </p>
      {fields.length > 0 ? (
        <div className="space-y-3">
          {fields.map((field, index) => (
            <div
              key={field.FID}
              className="flex items-center justify-between bg-white shadow-sm border border-gray-200 rounded-lg px-4 py-3"
            >
              <span className="flex-1 text-sm text-gray-700 break-all">
                {field.F}
              </span>
              <div className="flex items-center gap-1 flex-shrink-0">
                <Button
                  variant="ghost"
                  size="icon"
                  onClick={() => {
                    setEditingField(field);
                    setFieldName(field.F);
                    setShowAddField(true);
                  }}
                  className="h-8 w-8 text-sahi-blue hover:bg-blue-50 flex-shrink-0"
                >
                  <Pencil className="h-4 w-4" />
                </Button>
                <Button
                  variant="ghost"
                  size="icon"
                  onClick={() => {
                    setFieldToDelete(field);
                    setDeleteAlertOpen(true);
                  }}
                  className="text-sahi-red hover:bg-red-50 h-8 w-8"
                >
                  <Trash2 className="h-4 w-4" />
                </Button>
              </div>
            </div>
          ))}
        </div>
      ) : (
        <div className="no-records border border-gray-200 shadow-sm sahi-radius">
          No fields found.
        </div>
      )}
    </div>
  );

  const renderAddFieldForm = () => (
    <div className="space-y-4">
      <div className="space-y-2">
        <div className="flex justify-between items-center">
          <Label htmlFor="field-name" required={true}>
            Field Name
          </Label>
          {fieldName.length >= 45 && (
            <span className="text-xs text-gray-500">
              {50 - fieldName.length} characters left
            </span>
          )}
        </div>
        <Input
          id="field-name"
          value={fieldName}
          onChange={(e) => setFieldName(e.target.value)}
          placeholder="e.g. Quantity"
          className="h-11 text-sm border-2 border-gray-300 hover:border-gray-400 transition-all duration-200"
          autoComplete="off"
          autoFocus
          onKeyPress={(e) => {
            if (e.key === 'Enter' && fieldName.trim()) {
              editingField ? handleUpdateField() : handleAddField();
            }
          }}
          maxLength={50}
        />
      </div>
      <div className="flex flex-col sm:flex-row gap-3">
        <Button
          onClick={editingField ? handleUpdateField : handleAddField}
          disabled={loading}
          className="sahi-primary-button flex-1 sm:flex-none sm:min-w-[140px]"
        >
          <Check className="h-4 w-4" strokeWidth={3} />
          {loading ? (editingField ? 'Updating...' : 'Adding...') : (editingField ? 'Update Field' : 'Save Field')}
        </Button>
        <Button
          variant="outline"
          onClick={handleCloseAddForm}
          className="sahi-secondary-button flex-1 sm:flex-none sm:min-w-[100px]"
        >
          <X className="w-3 h-3" />
          Cancel
        </Button>
      </div>
    </div>
  );

  return (
    <>
      <Dialog open={open} onOpenChange={(isOpen) => !isOpen && handleClose()}>
        <DialogContent className="sm:max-w-md w-full">
          <DialogHeader>
            <DialogTitle className="font-semibold text-white">
              {showAddField 
                ? `${editingField ? 'Edit' : 'Add'} Field - ${threadTypeName}`
                : `${threadTypeName} Configuration`
              }
            </DialogTitle>
            <div className="flex items-center gap-2">
              {showAddField && editingField && (
                <Button
                  variant="ghost"
                  size="icon"
                  onClick={() => {
                    setFieldToDelete(editingField);
                    setDeleteAlertOpen(true);
                  }}
                  className="h-9 w-9 p-0 text-white hover:bg-white/20"
                >
                  <Trash2 className="h-5 w-5" />
                </Button>
              )}
              <Button
                variant="ghost"
                size="icon"
                onClick={showAddField ? handleCloseAddForm : handleClose}
                className="h-9 w-9 p-0 text-white hover:bg-white/20"
              >
                <X className="h-5 w-5" />
                <span className="sr-only">Close</span>
              </Button>
            </div>
          </DialogHeader>

          <div className="px-4 pb-4">
            {showAddField ? renderAddFieldForm() : (
              <>
                {renderFieldList()}
                <div className="flex flex-col sm:flex-row gap-3 mt-6">
                  <Button
                    onClick={() => setShowAddField(true)}
                    disabled={fields.length >= maxFields}
                    className="sahi-primary-button flex-1 sm:flex-none sm:min-w-[140px]"
                  >
                    <Plus className="h-4 w-4" />
                    Add Field ({fields.length}/{maxFields})
                  </Button>
                  <Button
                    variant="outline"
                    onClick={handleClose}
                    className="sahi-secondary-button flex-1 sm:flex-none sm:min-w-[100px]"
                  >
                    <X className="w-3 h-3" />
                    Close
                  </Button>
                </div>
              </>
            )}
          </div>
        </DialogContent>
      </Dialog>

      {/* Delete Confirmation Dialog */}
      <AlertBox
        open={deleteAlertOpen}
        onOpenChange={setDeleteAlertOpen}
        title="Delete Field?"
        description={`Are you sure you want to delete "${fieldToDelete?.F}"? This action cannot be undone.`}
        actions={[
          {
            label: 'Cancel',
            value: 'cancel',
            variant: 'cancel',
            className: 'sahi-secondary-button flex-1 sm:flex-none sm:min-w-[100px]',
            icon: <X className="w-3 h-3" />
          },
          {
            label: 'Delete Field',
            value: 'delete',
            variant: 'action',
            className: 'sahi-primary-button flex-1 sm:flex-none sm:min-w-[140px]',
            icon: <Trash2 className="h-4 w-4" />
          }
        ]}
        onAction={(action) => {
          if (action === 'delete') {
            handleDeleteField();
            if (editingField && editingField.FID === fieldToDelete?.FID) {
              handleCloseAddForm();
            }
          } else {
            setDeleteAlertOpen(false);
            setFieldToDelete(null);
          }
        }}
      />
    </>
  );
};

export default ThrFieldConfig;